/**
 * Scrolling vendor and partner bar on the home page.
 *
 * Each logo links to that brand's official site. Three things follow from the
 * links that did not matter while these were inert images — see the notes on
 * the duplicated rail and on pausing below. Getting them wrong would make the
 * bar actively worse for keyboard and screen-reader users than it was before.
 */

type Partner = { name: string; src: string; href: string };

const partners: Partner[] = [
  { name: "Rogue Fitness", src: "/media/rogue-fitness.svg", href: "https://www.roguefitness.com/" },
  // Hammer Strength is a Life Fitness brand but keeps its own site.
  { name: "Hammer Strength", src: "/media/hammer-strength.svg", href: "https://www.hammerstrength.com/" },
  { name: "Core Health & Fitness", src: "/media/core-health-fitness.svg", href: "https://www.corehandf.com/" },
  // Commercial Nautilus is a Core Health & Fitness brand - nautilus.com is the
  // separate consumer business, so this points at the commercial line instead.
  { name: "Nautilus", src: "/media/nautilus.svg", href: "https://www.corehandf.com/collections/nautilus/" },
  // The supplied life-fitness.svg is a wide wordmark sitting on a 1:1
  // 192.756x192.756 canvas, ~79% of which is empty, plus a full-canvas white
  // backing rect. Every other logo here is a wordmark cropped to its artwork,
  // so .vendor-logo img (max-width:100%; max-height:42px) sized this one by its
  // height and drew it at roughly a quarter the size of its neighbours.
  // life-fitness-tight.svg is the same paths with the backing rect removed and
  // the viewBox set to the artwork's bounding box - no CSS exception needed.
  { name: "Life Fitness", src: "/media/life-fitness-tight.svg", href: "https://www.lifefitness.com/" },
  // Same defect as Life Fitness, less extreme: a 6.1:1 wordmark on a 1.8:1
  // canvas, 24% of which is artwork, so it drew at 69x11px against a 150px
  // neighbour. Cropped to its bounding box it draws at ~150x25px.
  { name: "TRUE Fitness", src: "/media/true-fitness-tight.svg", href: "https://truefitness.com/" },
  { name: "Precor", src: "/media/precor.svg", href: "https://www.precor.com/" },
  { name: "Matrix Fitness", src: "/media/matrix-fitness.svg", href: "https://www.matrixfitness.com/" },
  { name: "Eleiko", src: "/media/eleiko.svg", href: "https://eleiko.com/" },
];

export function VendorMarquee() {
  return (
    <section className="vendor-marquee" aria-label="Selected Altitude vendor and partner brands">
      <div className="site-container vendor-marquee__head">
        <p className="eyebrow">Vendor network</p>
        <p>Equipment, facility, and operator partners we work alongside.</p>
      </div>

      <div className="vendor-marquee__viewport">
        <div className="vendor-marquee__rail">
          {/* The rail is the list twice over: the animation translates by
              exactly one copy's width, so the second set slides in behind the
              first and the loop has no visible seam.

              Only the first copy is real. The second is marked aria-hidden and
              removed from the tab order, because otherwise a keyboard user
              would tab through eighteen links - nine of them duplicates of
              what they just passed - and a screen reader would announce every
              brand twice. That cost nothing while these were plain images; it
              is a genuine defect once they are focusable. */}
          {partners.map(partner => (
            <PartnerLogo key={partner.name} partner={partner} />
          ))}
          {partners.map(partner => (
            <PartnerLogo key={`clone-${partner.name}`} partner={partner} duplicate />
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnerLogo({ partner, duplicate = false }: { partner: Partner; duplicate?: boolean }) {
  return (
    <a
      className="vendor-logo"
      href={partner.href}
      // These are third-party sites; opening in place would drop a visitor out
      // of the page mid-scroll. noopener is the one that matters - without it
      // the opened page gets a handle on this window through window.opener and
      // can navigate it somewhere else.
      target="_blank"
      rel="noopener noreferrer"
      aria-hidden={duplicate || undefined}
      tabIndex={duplicate ? -1 : undefined}
      aria-label={`${partner.name} (opens in a new tab)`}
    >
      {/* alt is empty because the surrounding link already carries the brand
          name. Repeating it here would have a screen reader say it twice. */}
      <img src={partner.src} alt="" loading="lazy" />
    </a>
  );
}
